export interface FormulaContext {
  data: any[];
  currentRow: number;
  currentCol: string;
}

export class FormulaEngine {
  private static instance: FormulaEngine;
  
  public static getInstance(): FormulaEngine {
    if (!FormulaEngine.instance) {
      FormulaEngine.instance = new FormulaEngine();
    }
    return FormulaEngine.instance;
  }

  private functions: Map<string, Function> = new Map();

  constructor() {
    this.registerBuiltInFunctions();
  }

  private registerBuiltInFunctions() {
    this.functions.set('SUM', this.sum);
    this.functions.set('AVERAGE', this.average);
    this.functions.set('AVG', this.average);
    this.functions.set('COUNT', this.count);
    this.functions.set('MAX', this.max);
    this.functions.set('MIN', this.min);
    this.functions.set('IF', this.ifFunction);
    this.functions.set('VLOOKUP', this.vlookup);
    this.functions.set('CONCATENATE', this.concatenate);
    this.functions.set('CONCAT', this.concatenate);
    this.functions.set('TODAY', this.today);
    this.functions.set('NOW', this.now);
    this.functions.set('ROUND', this.round);
    this.functions.set('UPPER', this.upper);
    this.functions.set('LOWER', this.lower);
    this.functions.set('LEN', this.length);
    this.functions.set('TRIM', this.trim);
  }

  public evaluate(formula: string, context: FormulaContext): any {
    try {
      if (!formula.startsWith('=')) {
        return formula;
      }

      const expression = formula.substring(1);
      return this.parseExpression(expression, context);
    } catch (error) {
      console.error('Formula evaluation error:', error);
      return '#ERROR!';
    }
  }

  private parseExpression(expression: string, context: FormulaContext): any {
    expression = expression.trim();

    if (this.isFunction(expression)) {
      return this.evaluateFunction(expression, context);
    }

    if (this.isCellReference(expression)) {
      return this.getCellValue(expression, context);
    }

    if (this.isRange(expression)) {
      return this.getRangeValues(expression, context);
    }

    if (this.isNumber(expression)) {
      return parseFloat(expression);
    }

    if (this.isString(expression)) {
      return expression.replace(/^["']|["']$/g, '');
    }

    return expression;
  }

  private isFunction(expression: string): boolean {
    return /^[A-Z]+\s*\(/.test(expression);
  }

  private isCellReference(expression: string): boolean {
    return /^[A-Z]+\d+$/.test(expression);
  }

  private isRange(expression: string): boolean {
    return /^[A-Z]+\d+:[A-Z]+\d+$/.test(expression);
  }

  private isNumber(expression: string): boolean {
    return !isNaN(parseFloat(expression)) && isFinite(parseFloat(expression));
  }

  private isString(expression: string): boolean {
    return /^["'].*["']$/.test(expression);
  }

  private evaluateFunction(expression: string, context: FormulaContext): any {
    const match = expression.match(/^([A-Z]+)\s*\((.*)\)$/);
    if (!match) return '#ERROR!';

    const [, functionName, argsString] = match;
    const func = this.functions.get(functionName);
    
    if (!func) {
      return `#NAME? (Unknown function: ${functionName})`;
    }

    const args = this.parseArguments(argsString, context);
    return func.call(this, ...args, context);
  }

  private parseArguments(argsString: string, context: FormulaContext): any[] {
    if (!argsString.trim()) return [];

    const args: any[] = [];
    let currentArg = '';
    let parenDepth = 0;
    let inQuotes = false;
    let quoteChar = '';

    for (let i = 0; i < argsString.length; i++) {
      const char = argsString[i];

      if ((char === '"' || char === "'") && !inQuotes) {
        inQuotes = true;
        quoteChar = char;
        currentArg += char;
      } else if (char === quoteChar && inQuotes) {
        inQuotes = false;
        quoteChar = '';
        currentArg += char;
      } else if (char === '(' && !inQuotes) {
        parenDepth++;
        currentArg += char;
      } else if (char === ')' && !inQuotes) {
        parenDepth--;
        currentArg += char;
      } else if (char === ',' && parenDepth === 0 && !inQuotes) {
        args.push(this.parseExpression(currentArg.trim(), context));
        currentArg = '';
      } else {
        currentArg += char;
      }
    }

    if (currentArg.trim()) {
      args.push(this.parseExpression(currentArg.trim(), context));
    }

    return args;
  }

  private getCellValue(cellRef: string, context: FormulaContext): any {
    const { col, row } = this.parseCellReference(cellRef);
    if (row < 0 || row >= context.data.length) return 0;
    return context.data[row][col] || 0;
  }

  private getRangeValues(range: string, context: FormulaContext): any[] {
    const [startCell, endCell] = range.split(':');
    const start = this.parseCellReference(startCell);
    const end = this.parseCellReference(endCell);

    const values: any[] = [];
    for (let row = start.row; row <= end.row; row++) {
      for (let colIndex = this.columnToIndex(start.col); colIndex <= this.columnToIndex(end.col); colIndex++) {
        const col = this.indexToColumn(colIndex);
        if (row >= 0 && row < context.data.length) {
          values.push(context.data[row][col] || 0);
        }
      }
    }
    return values;
  }

  private parseCellReference(cellRef: string): { col: string, row: number } {
    const match = cellRef.match(/^([A-Z]+)(\d+)$/);
    if (!match) throw new Error(`Invalid cell reference: ${cellRef}`);
    return { col: match[1], row: parseInt(match[2]) - 1 };
  }

  private columnToIndex(col: string): number {
    let result = 0;
    for (let i = 0; i < col.length; i++) {
      result = result * 26 + (col.charCodeAt(i) - 64);
    }
    return result - 1;
  }

  private indexToColumn(index: number): string {
    let result = '';
    index++;
    while (index > 0) {
      index--;
      result = String.fromCharCode(65 + (index % 26)) + result;
      index = Math.floor(index / 26);
    }
    return result;
  }

  private sum(...args: any[]): number {
    const values = this.flattenValues(args);
    return values.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  }

  private average(...args: any[]): number {
    const values = this.flattenValues(args);
    const numericValues = values.filter(val => !isNaN(parseFloat(val)));
    if (numericValues.length === 0) return 0;
    return this.sum(...args) / numericValues.length;
  }

  private count(...args: any[]): number {
    const values = this.flattenValues(args);
    return values.filter(val => val !== null && val !== undefined && val !== '').length;
  }

  private max(...args: any[]): number {
    const values = this.flattenValues(args).map(val => parseFloat(val)).filter(val => !isNaN(val));
    return values.length > 0 ? Math.max(...values) : 0;
  }

  private min(...args: any[]): number {
    const values = this.flattenValues(args).map(val => parseFloat(val)).filter(val => !isNaN(val));
    return values.length > 0 ? Math.min(...values) : 0;
  }

  private ifFunction(condition: any, trueValue: any, falseValue: any): any {
    return condition ? trueValue : falseValue;
  }

  private vlookup(lookupValue: any, tableArray: any[], colIndex: number, exactMatch: boolean = false): any {
    for (const row of tableArray) {
      if (Array.isArray(row) && row.length > colIndex) {
        if (exactMatch ? row[0] === lookupValue : row[0] >= lookupValue) {
          return row[colIndex];
        }
      }
    }
    return '#N/A';
  }

  private concatenate(...args: any[]): string {
    return args.map(arg => String(arg || '')).join('');
  }

  private today(): string {
    return new Date().toLocaleDateString();
  }

  private now(): string {
    return new Date().toLocaleString();
  }

  private round(number: number, digits: number = 0): number {
    return Math.round(number * Math.pow(10, digits)) / Math.pow(10, digits);
  }

  private upper(text: string): string {
    return String(text || '').toUpperCase();
  }

  private lower(text: string): string {
    return String(text || '').toLowerCase();
  }

  private length(text: string): number {
    return String(text || '').length;
  }

  private trim(text: string): string {
    return String(text || '').trim();
  }

  private flattenValues(args: any[]): any[] {
    const result: any[] = [];
    for (const arg of args) {
      if (Array.isArray(arg)) {
        result.push(...this.flattenValues(arg));
      } else {
        result.push(arg);
      }
    }
    return result;
  }
}
